// Activo Fijo → Configuración → Numeración de placas.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, Loader2, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { ActivoRow, Campo, ParametrosAF as TParam, af, afRpc, fieldCls } from './shared';

const preview = (prefijo: string, num: number, relleno: number) => `${prefijo || 'AF-'}${String(num || 1).padStart(Math.max(relleno || 1, 1), '0')}`;

export function PlacasAF() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<TParam[]>({ queryKey: ['af-parametros'], queryFn: () => af.list<TParam>('parametros', '?select=*&limit=1') });
  const actual = rows[0];
  const [form, setForm] = useState<TParam | null>(null);
  useEffect(() => { if (actual) setForm(actual); }, [actual]);
  const set = (p: Partial<TParam>) => setForm((f) => (f ? { ...f, ...p } : f));

  const { data: activos = [] } = useQuery<ActivoRow[]>({ queryKey: ['af-activos', 'activo', ''], queryFn: () => afRpc<ActivoRow[]>('af_activos', { _estado: 'activo', _busq: null }) });
  const [activoId, setActivoId] = useState('');

  const guardar = useMutation({
    mutationFn: () => { if (!form) throw new Error('Sin datos'); return af.update('parametros', form.id, { prefijo_placa: form.prefijo_placa, placa_relleno: Number(form.placa_relleno) || 1, siguiente_placa: Number(form.siguiente_placa) || 1 }); },
    onSuccess: () => { toast.success('Numeración guardada'); void qc.invalidateQueries({ queryKey: ['af-parametros'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const regenerar = useMutation({
    mutationFn: () => afRpc<any>('af_placa_generar', { _activo_id: activoId }),
    onSuccess: (r) => { toast.success(`Placa asignada: ${r.placa}`); setActivoId(''); void qc.invalidateQueries({ queryKey: ['af-parametros'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isFetching || !form) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Hash className="w-5 h-5" /> Numeración de placas</h1>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Formato de la placa automática</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Campo label="Prefijo"><input className={fieldCls} value={form.prefijo_placa} onChange={(e) => set({ prefijo_placa: e.target.value })} /></Campo>
            <Campo label="Dígitos (relleno)"><input type="number" min="1" max="12" className={fieldCls} value={form.placa_relleno} onChange={(e) => set({ placa_relleno: Number(e.target.value) })} /></Campo>
            <Campo label="Siguiente número"><input type="number" min="1" className={fieldCls} value={form.siguiente_placa} onChange={(e) => set({ siguiente_placa: Number(e.target.value) })} /></Campo>
          </div>
          <p className="text-sm">Próxima placa: <span className="font-mono font-semibold">{preview(form.prefijo_placa, form.siguiente_placa, form.placa_relleno)}</span></p>
          <div className="flex justify-end"><Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}><Save className="w-4 h-4 mr-1" /> Guardar</Button></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">(Re)generar placa de un activo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">Asigna la próxima placa automática al activo elegido (útil tras una carga inicial o para corregir una placa).</p>
          <div className="flex items-end gap-2">
            <Campo label="Activo" className="flex-1">
              <select className={fieldCls} value={activoId} onChange={(e) => setActivoId(e.target.value)}>
                <option value="">— Selecciona —</option>
                {activos.map((a) => <option key={a.id} value={a.id}>{a.placa} · {a.nombre}</option>)}
              </select>
            </Campo>
            <Button variant="outline" isLoading={regenerar.isPending} disabled={!activoId} onClick={async () => { if (await dlg.confirmar('¿Asignar una nueva placa automática a este activo?')) regenerar.mutate(); }}><RefreshCw className="w-4 h-4 mr-1" /> Generar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
